import { useState, useContext, useEffect } from "react";
import { useParams } from "react-router-dom";

import { toast } from "react-toastify";

// * Style
import style from "../../../IncomeVerificationSlide/SalarySlipVerification/SalarySlip.module.css";

// * Components
import DocumentPreview from "../../../../DocumentPreview/DocumentPreview";
import ImageInputField from "../../../../DataInputFields/ImageInputField/ImageInputField";
import ContinueButton from "../../../../Buttons/SlideContinueButton/ContinueButton";

// * Icons=
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";

// * API Handler
import { useMutation } from "@tanstack/react-query";
// * APIs
import { uploadCustomerDocument } from "../../../../../api/services/documents";
import {
  checkLeadEligibility,
  checkBREConfigureStatus,
} from "../../../../../api/services/BRE";
import { pushRealTimeLeadToParticipant } from "../../../../../api/services/Journey";

// * Helpers
import {
  convertBase64,
  getCookiePathFromURL,
  getLocationPermission,
} from "../../../../../helpers";
// * Contexts
import CustomerContext from "../../../../../context/CustomerContext";

export default function PaymentProof({
  switchToNextSlide,
  updateJourneyData,
  rejectLead,
  updateLastVisitSlide,
  removeAllCookies,
}) {
  const CookiePath = getCookiePathFromURL(window.location.href);
  const { apiKey, apiSecret } = useParams();
  const { customerDetails } = useContext(CustomerContext);

  const [documentStatus, setDocumentStatus] = useState({
    error: false,
    hasDocument: false,
  });
  const [document, setDocument] = useState([]);
  useEffect(() => {
    if (documentStatus.hasDocument)
      setDocumentStatus({ error: false, hasDocument: false });
  }, [document]);

  const updateJourneySlideToSuccess = () => {
    const slidePayload = {
      slideName: "Success : Journey Completed",
      slideIndex: 51,
      slideIcon: "Success",
    };
    // * Update Journey last visit slide
    updateLastVisitSlide(slidePayload);

    switchToNextSlide();
  };
  // * API to push realtime leads to participants
  const pushRealTimeLeadToParticipantAPI = useMutation({
    mutationFn: (Payload) => pushRealTimeLeadToParticipant(Payload),
    retry: false,
  });

  // * API to execute configured BRE for the Lead.
  const checkLeadEligibilityAPI = useMutation({
    mutationFn: (Payload) => checkLeadEligibility(Payload),
    onSuccess: (data) => handleLeadEligibilitySuccess(data),
    onError: (error) => handleLeadEligibilityError(error),
    retry: false,
  });
  const handleLeadEligibilitySuccess = (data) => {
    const response = data.data;

    if (!response.lead_move_final_status) {
      // * Update lead stage to reject and send reject SMS
      rejectLead();

      removeAllCookies(CookiePath);
    }

    // * Update Journey last visit slide
    updateJourneySlideToSuccess();
  };
  const handleLeadEligibilityError = () => {
    removeAllCookies(CookiePath);

    // * Update Journey last visit slide
    updateJourneySlideToSuccess();
  };
  const handleCheckLeadEligibility = () => {
    const data = {
      pipeline_id: customerDetails.pipelineID,
      lead_id: customerDetails.leadId,
    };
    const headers = { apiKey, apiSecret };
    const payload = {
      data,
      headers,
    };
    checkLeadEligibilityAPI.mutate(payload);
  };

  // * API to check whether BRE is configured for the Pipeline or not.
  const checkBREConfigureStatusAPI = useMutation({
    mutationFn: (payload) => checkBREConfigureStatus(payload),
    onSuccess: (data) => handleCheckBREConfigureStatusSuccess(data),
    onError: (data) => handleCheckBREConfigureStatusError(data),
  });
  const handleCheckBREConfigureStatusSuccess = (data) => {
    const BRE = data.data;

    if (BRE && Array.isArray(BRE) && BRE.length) {
      handleCheckLeadEligibility();
    } else {
      // * Login Lead
      const headers = {
        apiKey: apiKey,
        apiSecret: apiSecret,
        Authorization: `Bearer ${customerDetails?.authToken}`,
      };
      const payload = {
        headers,
        id: customerDetails?.appID,
      };

      pushRealTimeLeadToParticipantAPI.mutate(payload);

      // * Update Journey last visit slide
      updateJourneySlideToSuccess();
    }
  };
  const handleCheckBREConfigureStatusError = (error) => {
    console.log("ERROR : ", error);

    // * Update Journey last visit slide
    updateJourneySlideToSuccess();
  };
  const handleCheckBRECongiureStatus = () => {
    const pipelineId = customerDetails.pipelineID;
    // const leadId = customerDetails.leadId;

    const headers = {
      apiKey,
      apiSecret,
    };
    const data = {
      pipeline_id: pipelineId,
      // lead_id: leadId,
    };
    const payload = {
      data,
      headers,
    };
    checkBREConfigureStatusAPI.mutate(payload);
  };

  const saveDocument = async () => {
    if (!document.length) {
      return;
    }

    if (
      document.length &&
      (document[0]?.type === "application/pdf" ||
        document[0]?.type.includes("image")) &&
      !uploadCustomerDocumetAPI.isPending
    ) {
      setDocumentStatus({ ...documentStatus, error: false });

      const base64 = await convertBase64(document[0]);
      const headers = {
        apiKey,
        apiSecret,
      };
      let Payload = {
        contact_id: customerDetails.contactID,
        pipeline_id: customerDetails.pipelineID,
        lead_id: customerDetails.leadId,
        docUpload: base64,
        fileName: document[0].name,
        docType: "paymentProof",
        docNumber: "",
        source_name: "Journey",
        headers,
      };
      const location = await getLocationPermission();
      if (location) {
        Payload = { ...Payload, ...location };
      }

      if (document[0].type.includes("image")) {
        Payload.docFormat = "image";
      } else if (document[0].type === "application/pdf") {
        Payload.docFormat = "pdf";
      } else {
        toast.error("Please upload document in Image or PDF only.");
        return;
      }

      uploadCustomerDocumetAPI.mutate(Payload);
    }
  };

  // * API to upload Bank Statement only as document
  const uploadCustomerDocumetAPI = useMutation({
    mutationKey: ["uploadCustomerDocument"],
    mutationFn: (Payload) => uploadCustomerDocument(Payload),
    onSuccess: (data) => handleDocumentUploadSuccess(data),
    onError: (error) => handleDocumentUploadError(error),
    retry: false,
  });
  const handleDocumentUploadSuccess = (data) => {
    const leadData = {
      project_payment_proof_document_id: data.data.id,
    };

    updateJourneyData(leadData);

    handleCheckBRECongiureStatus();
  };
  const handleDocumentUploadError = (error) => {
    if (error.response.status === 401) {
      localStorage.removeItem("authToken");
      saveDocument();
    } else {
      toast.error("Something went wrong! please try again.");
    }
  };

  const validateDocs = () => {
    const { hasDocument } = documentStatus;

    if (hasDocument) {
      switchToNextSlide();
    } else if (!hasDocument && document.length === 0) {
      setDocumentStatus({ ...documentStatus, error: true });
    } else {
      saveDocument();
    }
  };

  return (
    <div className="carouselSlideWrapper salarySlipAndBankStatementSlideWrapper">
      <div className="typographyContainer">
        <h1>Payment Proof</h1>
        <p>Upload the proof of the payment paid to the developer.</p>
      </div>

      <div className="dataFieldsContainer imageInputContainer">
        <div className={style.documentInputBox}>
          {document?.length > 0
            ? document.map((doc) => (
                <DocumentPreview
                  key={doc.name}
                  document={doc}
                  removeDocument={() => setDocument([])}
                  removeTitle="Delete Bank Statement"
                />
              ))
            : null}

          <ImageInputField
            icon={
              <ReceiptOutlinedIcon
                style={{ fontSize: "45px", color: "rgb(189, 205, 255)" }}
              />
            }
            accept="image/jpg, image/jpeg, image/png, application/pdf"
            allowedFileTypes="pdf, jpeg, jpg, png"
            file={document}
            setFile={setDocument}
            isDocAvailable={documentStatus.error}
            isLoading={uploadCustomerDocumetAPI.isPending ? true : false}
            hasDocument={documentStatus.hasDocument}
            max={1}
          />
        </div>
      </div>

      <div className="imageInputContinueBtn">
        <ContinueButton
          label={
            documentStatus.hasDocument
              ? "Confirm & Continue"
              : document?.length
              ? "Continue"
              : "Upload"
          }
          loading={uploadCustomerDocumetAPI.isPending}
          handler={validateDocs}
          disabled={
            documentStatus.hasDocument || document?.length ? false : true
          }
        />
      </div>
    </div>
  );
}
